import { MDSDropdown } from '@marqvision/mds-v2';
import { useQueryParams } from '@marqvision/shared';
import { useCountryFilterQuery as useDomainNamesCountryFilterQuery } from '~/apis/domainNames';
import { useCountryFilterQuery as useWebsiteContentCountryFilterQuery } from '~/apis/websiteContent';
import { ChannelGroup } from '~/components/Filters/DynamicFilter/@types';
import { ResponsiveProps } from '~/components/Responsive/@types';
import { useLogger } from '~/hooks';
import { FilterQueryParams as DomainNamesFilterQueryParams } from '~/pages/DomainNames/@types';
import { useRequest as useDomainNamesRequest } from '~/pages/DomainNames/List/@hooks/useRequest';
import { FilterQueryParams as WebsiteContentFilterQueryParams } from '~/pages/WebsiteContent/_impersonation/@types';
import { useRequest as useWebsiteContentRequest } from '~/pages/WebsiteContent/_impersonation/List/@hooks/useRequest';
import { MarqFoundationEnumsCountryCodeEnum } from '~/swagger/data-contracts';
import { parseDropDown, parseValueToValueArray } from '~/utils';

interface Props extends ResponsiveProps {
  channel?: ChannelGroup;
  logPage?: string;
}

export const CountryFilter = (props: Props) => {
  const { size = 'medium', channel = 'domain names', logPage } = props;

  const { amplitudeLogging } = useLogger();

  // Channel별 hooks 선택
  const isDomainNames = channel === 'domain names';
  const { stringParams: domainNamesParams, setParams: setDomainNamesParams } =
    useQueryParams<DomainNamesFilterQueryParams>();
  const { stringParams: websiteContentParams, setParams: setWebsiteContentParams } =
    useQueryParams<WebsiteContentFilterQueryParams>();
  const {
    request: { country_filter: domainNamesCountryFilter, ...domainNamesRequest },
  } = useDomainNamesRequest();
  const {
    request: { country_filter: websiteContentCountryFilter, ...websiteContentRequest },
  } = useWebsiteContentRequest();

  // Channel별 파라미터와 request 선택
  const params = isDomainNames ? domainNamesParams : websiteContentParams;
  const setParams = isDomainNames ? setDomainNamesParams : setWebsiteContentParams;

  // Channel별 API hook 사용
  const domainNamesQuery = useDomainNamesCountryFilterQuery(domainNamesRequest, {
    select: parseDropDown<MarqFoundationEnumsCountryCodeEnum>,
    enabled: isDomainNames,
  });
  const websiteContentQuery = useWebsiteContentCountryFilterQuery(websiteContentRequest, {
    select: parseDropDown<MarqFoundationEnumsCountryCodeEnum>,
    enabled: !isDomainNames,
  });

  const data = isDomainNames ? domainNamesQuery.data : websiteContentQuery.data;
  const list = data?.list || [];

  const selectedCountries = params.country_filter ? parseValueToValueArray(params.country_filter) : [];

  const handleChange = (value: MarqFoundationEnumsCountryCodeEnum[]) => {
    setParams({ country_filter: value });

    if (value.length > 0) {
      amplitudeLogging('applied filter', {
        Type: 'country',
        Channel: channel,
        Page: logPage || (isDomainNames ? 'domain list' : 'website content list'),
      });
    }
  };

  return (
    <MDSDropdown
      label="Country"
      modules={[
        'hide-select-all',
        'search',
        {
          type: 'filter-button',
          size,
        },
      ]}
      value={selectedCountries}
      list={list}
      onChange={handleChange}
    />
  );
};
